import { type Registry as Services, service } from '@ember/service';
import Model, { attr, type HasMany, hasMany } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type { Ota } from 'frontend-utils/models';

export type Offset = { type: 'absolute' | 'relative'; value: number };
export type FormattedOffset = { value: string | null };

/**
 * Represents the channel-pricing subscription configuration fetched
 * based on a subscription ID.
 */
export default class ChannelPricingSubscriptionConfig extends Model {
  [Type] = 'channel-pricing-subscription-config' as const;

  @hasMany('ota', { async: false, inverse: null })
  declare channels: HasMany<Ota>;

  // BE will return as all minor channels of the subscription. These values will be strings, as they'll probably not overlap with our OTAs
  // Can be values like e.g. "Voordeeluitjes.nl". The strings we receive should be OK to visualise to users.
  @attr() declare minorChannels: string[];

  /**
   * Record of available offsets in channels.
   */
  @attr() declare offsets: Record<string, Offset>;

  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  @service declare intl: Services['intl'];

  get formattedOffsets(): Record<string, FormattedOffset> {
    const formattedOffsets: Record<string, FormattedOffset> = {};

    if (!this.offsets) {
      return formattedOffsets;
    }

    Object.entries(this.offsets).forEach(([key, offset]) => {
      if (offset.type === 'absolute') {
        formattedOffsets[key] = {
          value: this.hotelAndMarketService.formatRate(offset.value, true),
        };
      } else {
        formattedOffsets[key] = {
          value: this.intl.formatNumber(offset.value, { style: 'percent' }),
        };
      }
    });

    return formattedOffsets;
  }
}
